import { useEffect, useState } from 'react';
import { UseFormRegister, FieldValues, DeepMap, FieldError, UseFormSetValue } from 'react-hook-form';
import { TSelectOption } from '../../../util/types';
import EditButton from '../EditButton';
import generateErrorMarkup from '../generateErrorMarkup';

type Props = {
  name: string;
  label: string;
  options: TSelectOption[];
  optionsUrl?: string;
  // Optional default value (will revert to formData[name] if none)
  defaultValue?: string;
  disclaimer?: React.ReactElement;
  showEditBtn: boolean;
  editable: boolean;
  validationObj?: any; // TODO: add types
  hidden?: boolean;

  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors: DeepMap<FieldValues, FieldError>;
  formData: any;
  returnToStep?: (name: string) => void;
}

/**
 * A Select component
 * @param props
 */
let Select = (props: Props) => {

  // https://www.carlrippon.com/drop-down-data-binding-with-react-hooks/
  // TODO: 'loading' never shows because i set the first one to be props.label
  const [items, setItems] = useState(props.optionsUrl ? [{ label: 'loading...', value: '' }] : props.options);
  const [loading, setLoading] = useState(props.optionsUrl ? true : false);

  const defaultValue = props.defaultValue ?? props.formData[props.name] ?? '';

  useEffect(() => {
    let unmounted = false;

    async function getOptions() {
      // TODO: auth this at all?
      const isDev = process.env.NODE_ENV !== 'production';
      const url = `${isDev ? 'http://localhost:8000' : ''}${props.optionsUrl}`;
      const response = await fetch(url);
      const parsedOptions = await response.json();
      if (!unmounted) {
        setItems(parsedOptions);
        setLoading(false);

        // console.log(`Setting the Select value to '${props.defaultValue}'`);
        props.setValue(props.name, defaultValue);
      }
    };

    if (props.optionsUrl) {
      getOptions();
    }

    return () => {
      unmounted = true;
    };
  }, [props.optionsUrl, props.defaultValue, props, defaultValue]);

  // TODO: is this fine or do i need to save state somehow?
  if (props.hidden) return null;

  // TODO: react-hook-form probably has a better way of passing refs around
  // but this seems to work for now...
  const { register, errors } = props;

  const clickEditBtn = (e: React.MouseEvent) => {
    // Only has one if added for reviewMode
    if (props.returnToStep) props.returnToStep(props.name);
  };

  // Add default value with label
  const options = [
    <option
      value=''
      key={0}
    >
      {props.validationObj?.required ? `${props.label}*` : props.label}
    </option>
  ];
  options.push(...items.map((option, i) => {
    const optionEl =
      <option
        value={option.value}
        key={i + 1}
      >
        {option.label}
      </option>
    return optionEl;
  }));

  const elem =
  <div className="Select">
    <label>
      <span className="sr-only">
        {`${props.label}:`}
      </span>
      <div className='Select__Wrapper'>
        <select {...register(props.name, props.validationObj)}
          defaultValue={defaultValue}
          className={errors[props.name] ? 'parsley-error' : undefined}
          aria-describedby={errors[props.name] ? `${props.name}-errors` : undefined}
          // NOTE: getValues doesn't return the value if the input is disabled!
          disabled={!props.editable || loading}
        >
          {options}
        </select>
        { props.showEditBtn ? <EditButton onClick={clickEditBtn} /> : null }
      </div>
    </label>
    { generateErrorMarkup(errors, props.name) }
    {props.disclaimer ?
      <div className="Field__Disclaimer">
        {props.disclaimer}
      </div>
    : null}
  </div>;

  return (
    <>
      {elem}
    </>
  );
};

export default Select;
